import { api } from './api';
import { auth } from '../firebase/firebaseConfig';

/**
 * Get current user's subscription status
 * @returns {Promise<Object>} Subscription status
 */
export const getSubscriptionStatus = async () => {
    try {
        const user = auth.currentUser;
        if (!user) {
            return null; // Return null instead of throwing - no error logging
        }

        const token = await user.getIdToken();
        const response = await api.get('/subscriptions/me', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return response.data;
    } catch (error) {
        // Only log errors if user is authenticated (actual errors, not missing auth)
        if (auth.currentUser) {
            console.error('❌ Error getting subscription status:', error.response?.data?.message || error.message);
        }
        return null; // Return null instead of throwing for graceful handling
    }
};

/**
 * Get recipe quota information
 * @returns {Promise<Object>} Quota information
 */
export const getRecipeQuota = async () => {
    try {
        const user = auth.currentUser;
        if (!user) {
            return null; // Return null instead of throwing - no error logging
        }

        const token = await user.getIdToken();
        const response = await api.get('/subscriptions/quota', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return response.data;
    } catch (error) {
        // Only log errors if user is authenticated (actual errors, not missing auth)
        if (auth.currentUser) {
            console.error('❌ Error getting recipe quota:', error.response?.data?.message || error.message);
        }
        return null; // Return null instead of throwing for graceful handling
    }
};

/**
 * Create Stripe checkout session for subscription
 * @param {string} planType - 'basic' or 'unlimited'
 * @returns {Promise<Object>} Checkout session details
 */
export const createCheckoutSession = async (planType) => {
    try {
        const user = auth.currentUser;
        if (!user) {
            throw new Error('No authenticated user found');
        }

        const token = await user.getIdToken();
        const response = await api.post('/subscriptions/checkout', 
            { planType },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        return response.data;
    } catch (error) {
        console.error('❌ Error creating checkout session:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Create customer portal session for subscription management
 * @returns {Promise<Object>} Portal session details
 */
export const openCustomerPortal = async () => {
    try {
        const user = auth.currentUser;
        if (!user) {
            throw new Error('No authenticated user found');
        }

        const token = await user.getIdToken();
        const response = await api.post('/subscriptions/portal', {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return response.data;
    } catch (error) {
        console.error('❌ Error opening customer portal:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Cancel current subscription
 * @returns {Promise<Object>} Cancellation confirmation
 */
export const cancelSubscription = async () => {
    try {
        const user = auth.currentUser;
        if (!user) {
            throw new Error('No authenticated user found');
        }

        const token = await user.getIdToken();
        const response = await api.post('/subscriptions/cancel', {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return response.data;
    } catch (error) {
        console.error('❌ Error canceling subscription:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Check if user can create recipes (quota check)
 * @returns {Promise<Object>} Quota check result
 */
export const checkCanCreateRecipe = async () => {
    try {
        const quotaInfo = await getRecipeQuota();
        
        return {
            canCreate: quotaInfo.isUnlimited || quotaInfo.used < quotaInfo.quota,
            quota: quotaInfo.quota,
            used: quotaInfo.used,
            remaining: quotaInfo.remaining,
            isUnlimited: quotaInfo.isUnlimited,
            planType: quotaInfo.planType
        };
    } catch (error) {
        console.error('❌ Error checking recipe quota:', error);
        // Default to allowing creation if check fails (graceful degradation)
        return {
            canCreate: true,
            quota: 4,
            used: 0,
            remaining: 4,
            isUnlimited: false,
            planType: 'free'
        };
    }
};

/**
 * Format quota display text
 * @param {Object} quotaInfo - Quota information object
 * @returns {string} Formatted quota text
 */
export const formatQuotaText = (quotaInfo) => {
    if (!quotaInfo) return 'Loading...';
    
    if (quotaInfo.isUnlimited) {
        return 'Unlimited recipes';
    }
    
    return `${quotaInfo.used}/${quotaInfo.quota} recipes`;
};

/**
 * Get plan display name
 * @param {string} planType - Plan type
 * @returns {string} Display name
 */
export const getPlanDisplayName = (planType) => {
    switch (planType) {
        case 'free':
            return 'Free';
        case 'basic':
            return 'Basic';
        case 'unlimited':
            return 'Unlimited';
        case 'admin':
            return 'Admin';
        case 'family':
            return 'Family';
        case 'beta':
            return 'Beta Tester';
        default:
            return 'Unknown';
    }
};

/**
 * Get plan pricing information
 * @param {string} planType - Plan type
 * @returns {Object} Pricing info
 */
export const getPlanPricing = (planType) => {
    switch (planType) {
        case 'free':
            return {
                price: 0,
                period: 'forever',
                recipes: -1, // Unlimited recipes
                features: ['Unlimited recipes', 'Basic features']
            };
        case 'basic':
            return {
                price: 9.99, // This should match your Stripe price
                period: 'month',
                recipes: -1, // Unlimited recipes
                features: ['Unlimited recipes', 'Chef agent features', 'Custom chef interactions', 'AI tips & reminders', 'Chef summaries', 'All basic features']
            };
        case 'unlimited':
            return {
                price: 25.99, // This should match your Stripe price
                period: 'month',
                recipes: -1,
                features: ['Unlimited recipes', 'Batch baking', 'Advanced analytics', 'Priority support']
            };
        case 'admin':
            return {
                price: 0,
                period: 'forever',
                recipes: -1,
                features: ['Unlimited recipes', 'All premium features', 'Admin privileges']
            };
        case 'family':
            return {
                price: 0,
                period: 'forever',
                recipes: -1,
                features: ['Unlimited recipes', 'All premium features', 'Family access']
            };
        case 'beta':
            return {
                price: 0,
                period: 'forever',
                recipes: -1,
                features: ['Unlimited recipes', 'All premium features', 'Beta testing access', 'Early feature access']
            };
        default:
            return {
                price: 0,
                period: 'forever',
                recipes: 0,
                features: []
            };
    }
};
